import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { getNotificationMeta as _getNotificationMeta } from '../hooks/useNotifications';

export interface Notification {
  id: string;
  user_id: string;
  type: string;
  title: string;
  body: string | null;
  read_at: string | null;
  data: Record<string, unknown>;
  created_at: string;
}

interface NotificationsContextValue {
  notifications: Notification[];
  loading: boolean;
  unreadCount: number;
  toastNotification: Notification | null;
  markAllRead: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  dismissToast: () => void;
  refetch: () => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

export const NotificationsProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [toastNotification, setToastNotification] = useState<Notification | null>(null);
  const prevUserIdRef = useRef<string | undefined>(undefined);

  // Clear stale data on user change
  useEffect(() => {
    const currentUserId = user?.id;
    if (prevUserIdRef.current !== currentUserId) {
      if (prevUserIdRef.current !== undefined || currentUserId === undefined) {
        setNotifications([]);
        setToastNotification(null);
      }
      prevUserIdRef.current = currentUserId;
    }
  }, [user?.id]);

  const fetchNotifications = useCallback(async () => {
    if (!user) {
      setNotifications([]);
      setLoading(false);
      return;
    }
    const { data } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(50);
    const safeData = ((data as Notification[]) ?? []).filter((n) => n.user_id === user.id);
    setNotifications(safeData);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchNotifications();
    if (!user) return;

    const channel = supabase
      .channel(`notif-ctx-${user.id}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
        (payload) => {
          const newNotif = payload.new as Notification;
          if (newNotif.user_id === user.id) {
            setNotifications((prev) => [newNotif, ...prev]);
            setToastNotification(newNotif);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, fetchNotifications]);

  const dismissToast = useCallback(() => setToastNotification(null), []);

  const markAllRead = useCallback(async () => {
    if (!user) return;
    const unreadIds = notifications.filter((n) => !n.read_at).map((n) => n.id);
    if (unreadIds.length === 0) return;
    const now = new Date().toISOString();
    await supabase.from('notifications').update({ read_at: now }).in('id', unreadIds).eq('user_id', user.id);
    setNotifications((prev) => prev.map((n) => ({ ...n, read_at: n.read_at ?? now })));
  }, [user, notifications]);

  const markRead = useCallback(async (id: string) => {
    if (!user) return;
    const now = new Date().toISOString();
    await supabase.from('notifications').update({ read_at: now }).eq('id', id).eq('user_id', user.id);
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read_at: now } : n)));

    const notif = notifications.find((n) => n.id === id);
    if (notif?.type === 'broadcast' && notif.data?.broadcast_id && !notif.read_at) {
      const broadcastId = notif.data.broadcast_id as string;
      await supabase.from('broadcast_recipients').update({ read_at: now }).eq('broadcast_id', broadcastId).eq('user_id', user.id);
      await supabase.rpc('increment_broadcast_read_count', { broadcast_id_param: broadcastId });
    }
  }, [user, notifications]);

  const unreadCount = notifications.filter((n) => !n.read_at).length;

  return (
    <NotificationsContext.Provider value={{ notifications, loading, unreadCount, toastNotification, markAllRead, markRead, dismissToast, refetch: fetchNotifications }}>
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotificationsContext = (): NotificationsContextValue => {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error('useNotificationsContext must be used within NotificationsProvider');
  return ctx;
};
